import React, { useState, useMemo } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import type {
  WorkflowQualityIntegrationProps,
  QualityGate,
  QualityGateStatus,
  AutomatedQualityWorkflow,
  WorkflowQualityData
} from '../../types/advanced_content_quality_ui';

// Lifecycle stage styling
const STAGE_STYLES = {
  draft: { bg: 'bg-gray-100', text: 'text-gray-800', icon: '📝' },
  review: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '👀' },
  approved: { bg: 'bg-green-100', text: 'text-green-800', icon: '✅' },
  published: { bg: 'bg-blue-100', text: 'text-blue-800', icon: '🚀' },
  archived: { bg: 'bg-purple-100', text: 'text-purple-800', icon: '📦' }
};

// Gate status styling
const GATE_STATUS_STYLES = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: '⏳' },
  passed: { bg: 'bg-green-100', text: 'text-green-800', icon: '✅' },
  failed: { bg: 'bg-red-100', text: 'text-red-800', icon: '❌' },
  skipped: { bg: 'bg-gray-100', text: 'text-gray-800', icon: '⏭️' }
};

export const WorkflowQualityIntegration: React.FC<WorkflowQualityIntegrationProps> = ({
  workflowData,
  contentId,
  allowConfiguration = false,
  showAutomation = true,
  onWorkflowUpdate
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'gates' | 'automation' | 'lifecycle' | 'optimization'>('overview');
  const [configMode, setConfigMode] = useState(false);
  const [updatingWorkflow, setUpdatingWorkflow] = useState(false);
  const [selectedGate, setSelectedGate] = useState<string | null>(null);

  // Calculate workflow efficiency metrics
  const efficiencyMetrics = useMemo(() => {
    const { workflowAutomation } = workflowData;
    return {
      automationRate: workflowAutomation.workflowEfficiency.automationRate,
      avgProcessingTime: workflowAutomation.workflowEfficiency.averageProcessingTime,
      interventionRate: workflowAutomation.workflowEfficiency.interventionRate,
      qualityConsistency: workflowAutomation.workflowEfficiency.qualityConsistency,
      costEffectiveness: workflowAutomation.workflowEfficiency.costEffectiveness
    };
  }, [workflowData.workflowAutomation.workflowEfficiency]);

  // Get current lifecycle stage info
  const currentStageInfo = useMemo(() => {
    const { currentStage } = workflowData.lifecycleQualityTracking;
    const stageStyle = STAGE_STYLES[currentStage];
    return { stage: currentStage, ...stageStyle };
  }, [workflowData.lifecycleQualityTracking.currentStage]);

  // Handle workflow configuration update
  const handleWorkflowUpdate = async (updates: Partial<WorkflowQualityData>) => {
    if (!onWorkflowUpdate) return;

    setUpdatingWorkflow(true);
    try {
      // Merge updates with existing data to ensure all required fields are present
      const completeUpdate: WorkflowQualityData = {
        ...workflowData,
        ...updates
      };
      await onWorkflowUpdate(completeUpdate);
    } catch (error) {
      console.error('Error updating workflow:', error);
    } finally {
      setUpdatingWorkflow(false);
    }
  };

  // Render quality gate card
  const renderQualityGate = (gate: QualityGate, status?: QualityGateStatus) => {
    const gateStatus = status?.status || 'pending';
    const statusStyle = GATE_STATUS_STYLES[gateStatus];
    
    return (
      <Card
        key={gate.id}
        className={`p-4 cursor-pointer transition-all duration-200 hover:shadow-md ${
          selectedGate === gate.id ? 'ring-2 ring-blue-500' : ''
        } ${statusStyle.bg}`}
        onClick={() => setSelectedGate(selectedGate === gate.id ? null : gate.id)}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <span className="text-lg">{statusStyle.icon}</span>
            <div>
              <h3 className={`font-semibold ${statusStyle.text}`}>{gate.name}</h3>
              <p className="text-sm text-gray-600">{gate.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className={`px-2 py-1 rounded text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
              {gateStatus.toUpperCase()}
            </span>
            {gate.required && (
              <span className="px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                Required
              </span>
            )}
          </div>
        </div>

        {/* Gate Criteria */}
        <div className="space-y-2 mb-3">
          {gate.criteria.map((criteria, index) => {
            const result = status?.results?.find(r => r.criteria === criteria);
            const passed = result?.passed ?? false;
            
            return (
              <div key={index} className="flex items-center justify-between text-sm">
                <span className="text-gray-700">{criteria.metric}</span>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600">
                    {criteria.operator} {criteria.threshold}
                  </span>
                  {result && (
                    <span className={`px-2 py-1 rounded text-xs ${
                      passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {result.actualValue}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Expanded Details */}
        {selectedGate === gate.id && (
          <div className="pt-3 border-t border-gray-200 space-y-3">
            <div>
              <div className="text-sm font-medium text-gray-900 mb-1">Stage</div>
              <div className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                STAGE_STYLES[gate.stage].bg
              } ${STAGE_STYLES[gate.stage].text}`}>
                <span className="mr-1">{STAGE_STYLES[gate.stage].icon}</span>
                {gate.stage}
              </div>
            </div>

            {status?.decision && (
              <div>
                <div className="text-sm font-medium text-gray-900 mb-1">Decision</div>
                <div className="text-sm text-gray-700 bg-white p-2 rounded border">
                  {status.decision}
                </div>
              </div>
            )}

            {allowConfiguration && (
              <div className="flex space-x-2 pt-2">
                <Button size="small" variant="ghost">
                  Configure
                </Button>
                {gate.automatable && (
                  <Button size="small" variant="ghost">
                    Enable Auto
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </Card>
    );
  };

  // Render automated workflow card
  const renderAutomatedWorkflow = (workflow: AutomatedQualityWorkflow) => {
    return (
      <Card key={workflow.id} className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${
              workflow.enabled ? 'bg-green-500' : 'bg-gray-400'
            }`} />
            <div>
              <h3 className="font-semibold text-gray-900">{workflow.name}</h3>
              <p className="text-sm text-gray-600">{workflow.description}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-sm text-gray-600">Success Rate</div>
            <div className="text-lg font-semibold text-green-600">
              {(workflow.successRate * 100).toFixed(1)}%
            </div>
          </div>
        </div>

        {/* Workflow Steps Summary */}
        <div className="mb-3">
          <div className="text-sm text-gray-600 mb-2">Steps: {workflow.steps.length}</div>
          <div className="flex space-x-1">
            {workflow.steps.map((step, index) => (
              <div
                key={step.id}
                className="w-4 h-2 rounded bg-blue-200"
                title={step.name}
              />
            ))}
          </div>
        </div>

        {/* Controls */}
        {allowConfiguration && (
          <div className="flex space-x-2">
            <Button
              size="small"
              variant={workflow.enabled ? "danger" : "success"}
              onClick={() => {
                // Toggle workflow enabled status
                handleWorkflowUpdate({
                  workflowAutomation: {
                    ...workflowData.workflowAutomation,
                    automatedWorkflows: workflowData.workflowAutomation.automatedWorkflows.map(w =>
                      w.id === workflow.id ? { ...w, enabled: !w.enabled } : w
                    )
                  }
                });
              }}
            >
              {workflow.enabled ? 'Disable' : 'Enable'}
            </Button>
            <Button size="small" variant="ghost">
              Configure
            </Button>
          </div>
        )}
      </Card>
    );
  };

  // Render overview tab
  const renderOverviewTab = () => {
    return (
      <div className="space-y-6">
        {/* Current Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">{currentStageInfo.icon}</span>
              <div>
                <div className="text-sm text-gray-600">Current Stage</div>
                <div className={`font-semibold capitalize ${currentStageInfo.text}`}>
                  {currentStageInfo.stage}
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">🤖</span>
              <div>
                <div className="text-sm text-gray-600">Automation Rate</div>
                <div className="text-lg font-semibold text-blue-600">
                  {(efficiencyMetrics.automationRate * 100).toFixed(1)}%
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">⚡</span>
              <div>
                <div className="text-sm text-gray-600">Avg Processing</div>
                <div className="text-lg font-semibold text-green-600">
                  {efficiencyMetrics.avgProcessingTime.toFixed(0)}min
                </div>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center space-x-3">
              <span className="text-2xl">🎯</span>
              <div>
                <div className="text-sm text-gray-600">Quality Consistency</div>
                <div className="text-lg font-semibold text-purple-600">
                  {(efficiencyMetrics.qualityConsistency * 100).toFixed(1)}%
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Active Quality Gates */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Quality Gates</h3>
          <div className="space-y-3">
            {workflowData.qualityGateConfiguration.gates
              .filter(gate => gate.stage === currentStageInfo.stage)
              .map(gate => {
                const status = workflowData.qualityGateConfiguration.currentGateStatus;
                return renderQualityGate(gate, gate.id === status?.gate.id ? status : undefined);
              })}
          </div>
        </Card>

        {/* Recent Manual Interventions */}
        {workflowData.workflowAutomation.manualInterventions.length > 0 && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Interventions</h3>
            <div className="space-y-3">
              {workflowData.workflowAutomation.manualInterventions.slice(0, 3).map((intervention, index) => (
                <div key={index} className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-yellow-800 capitalize">
                      {intervention.type.replace('_', ' ')}
                    </span>
                    <span className="text-sm text-yellow-600">
                      {intervention.timestamp.toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-yellow-700 mb-1">{intervention.reason}</p>
                  <p className="text-xs text-yellow-600">By: {intervention.contributor}</p>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    );
  };

  // Render gates tab
  const renderGatesTab = () => {
    const { gates, gateConfiguration } = workflowData.qualityGateConfiguration;
    
    return (
      <div className="space-y-6">
        {/* Gate Configuration Summary */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Quality Gates Configuration</h3>
            {allowConfiguration && (
              <Button
                size="small"
                onClick={() => setConfigMode(!configMode)}
              >
                {configMode ? 'View Mode' : 'Configure'}
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{gates.length}</div>
              <div className="text-sm text-gray-600">Total Gates</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {gateConfiguration.enabledGates.length}
              </div>
              <div className="text-sm text-gray-600">Enabled</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {gates.filter(g => g.automatable).length}
              </div>
              <div className="text-sm text-gray-600">Automatable</div>
            </div>
          </div>
        </Card>

        {/* Gates by Stage */}
        {(['draft', 'review', 'approved', 'published'] as const).map(stage => {
          const stageGates = gates.filter(gate => gate.stage === stage);
          if (stageGates.length === 0) return null;

          return (
            <Card key={stage} className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <span className="text-xl">{STAGE_STYLES[stage].icon}</span>
                <h3 className="text-lg font-semibold text-gray-900 capitalize">{stage} Stage</h3>
                <span className="text-sm text-gray-500">({stageGates.length} gates)</span>
              </div>
              
              <div className="space-y-3">
                {stageGates.map(gate => renderQualityGate(gate))}
              </div>
            </Card>
          );
        })}
      </div>
    );
  };

  // Render automation tab
  const renderAutomationTab = () => {
    if (!showAutomation) {
      return (
        <Card className="p-8 text-center">
          <span className="text-4xl mb-4 block">🤖</span>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Automation Disabled</h3>
          <p className="text-gray-600">Enable automation view to manage automated workflows.</p>
        </Card>
      );
    }

    const { automatedWorkflows, workflowEfficiency, optimizationSuggestions } = workflowData.workflowAutomation;
    
    return (
      <div className="space-y-6">
        {/* Automation Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{automatedWorkflows.length}</div>
              <div className="text-sm text-gray-600">Active Workflows</div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {(workflowEfficiency.automationRate * 100).toFixed(0)}%
              </div>
              <div className="text-sm text-gray-600">Automation Rate</div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {(workflowEfficiency.interventionRate * 100).toFixed(1)}%
              </div>
              <div className="text-sm text-gray-600">Intervention Rate</div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {workflowEfficiency.costEffectiveness.toFixed(1)}x
              </div>
              <div className="text-sm text-gray-600">Cost Effectiveness</div>
            </div>
          </Card>
        </div>

        {/* Automated Workflows */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Automated Workflows</h3>
          <div className="space-y-4">
            {automatedWorkflows.map(workflow => renderAutomatedWorkflow(workflow))}
          </div>
        </Card>

        {/* Optimization Suggestions */}
        {optimizationSuggestions.length > 0 && (
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Optimization Suggestions</h3>
            <div className="space-y-3">
              {optimizationSuggestions.map((suggestion, index) => (
                <div key={index} className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-blue-900">{suggestion.area}</h4>
                    <span className="text-sm text-blue-700">ROI: {suggestion.roi.toFixed(1)}x</span>
                  </div>
                  <p className="text-sm text-blue-800 mb-2">{suggestion.suggestion}</p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-blue-600">{suggestion.expectedBenefit}</span>
                    <span className={`px-2 py-1 rounded text-xs ${
                      suggestion.implementationComplexity === 'low' ? 'bg-green-100 text-green-800' :
                      suggestion.implementationComplexity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {suggestion.implementationComplexity} complexity
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    );
  };

  // Tab configuration
  const tabs = [
    { id: 'overview', label: 'Overview', icon: '📊' },
    { id: 'gates', label: 'Quality Gates', icon: '🚪' },
    ...(showAutomation ? [{ id: 'automation', label: 'Automation', icon: '🤖' }] : []),
    { id: 'lifecycle', label: 'Lifecycle', icon: '🔄' },
    { id: 'optimization', label: 'Optimization', icon: '⚡' }
  ] as const;

  return (
    <div className="workflow-quality-integration space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Workflow Quality Integration</h2>
          <p className="text-gray-600 mt-1">Quality management throughout content lifecycle</p>
        </div>
        
        {allowConfiguration && (
          <Button
            onClick={() => setConfigMode(!configMode)}
            disabled={updatingWorkflow}
          >
            {updatingWorkflow ? (
              <>
                <LoadingSpinner size="sm" />
                <span className="ml-2">Updating...</span>
              </>
            ) : configMode ? (
              'Save Configuration'
            ) : (
              'Configure Workflow'
            )}
          </Button>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'gates' && renderGatesTab()}
        {activeTab === 'automation' && renderAutomationTab()}
        {activeTab === 'lifecycle' && renderOverviewTab()} {/* Reuse overview for now */}
        {activeTab === 'optimization' && renderAutomationTab()} {/* Reuse automation for now */}
      </div>
    </div>
  );
};

export default WorkflowQualityIntegration;
